import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Card, Checkbox, ExpandableCard, Grid, Row, Stack, Text, TextButton } from 'library/ui';
import { IBusinessLocation } from 'models';
import { IPermission } from './models';
import { IPermissionGroup, groupPermissionsByProduct, parentPermissionId } from './permissionsByLocation';

export const ALL_LOCATIONS_KEY = 0;

export type PermissionsByLocation = Record<number, number[]>;

interface ILocationPermissionsProps {
    availableLocations: IBusinessLocation[];
    selectedLocations: number[];
    allLocationsSelected: boolean;
    availablePermissions: IPermission[];
    permissionsByLocation: PermissionsByLocation;
    onToggle: (locationKey: number, permissionId: number) => void;
    onCopyToAll: (fromLocationKey: number) => void;
}

export default function LocationPermissions({
    availableLocations,
    selectedLocations,
    allLocationsSelected,
    availablePermissions,
    permissionsByLocation,
    onToggle,
    onCopyToAll,
}: ILocationPermissionsProps) {
    const rows: { key: number; label: string }[] = allLocationsSelected
        ? [{ key: ALL_LOCATIONS_KEY, label: 'All Locations' }]
        : availableLocations.filter((x) => selectedLocations.includes(x.id)).map((x) => ({ key: x.id, label: x.name }));

    const groups = groupPermissionsByProduct(availablePermissions);
    const collapsible = rows.length > 1;
    const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
    const previousKeys = useRef<number[]>([]);
    const rowKeySignature = rows.map((row) => row.key).join(',');

    useEffect(() => {
        const keys = rows.map((row) => row.key);
        const added = keys.filter((key) => !previousKeys.current.includes(key));
        if (previousKeys.current.length > 0 && added.length) {
            setExpandedKeys((prev) => [...prev, ...added]);
        }
        previousKeys.current = keys;
    }, [rowKeySignature]);

    function isExpanded(locationKey: number) {
        return !collapsible || expandedKeys.includes(locationKey);
    }

    function toggleExpanded(locationKey: number) {
        setExpandedKeys((prev) =>
            prev.includes(locationKey) ? prev.filter((key) => key !== locationKey) : [...prev, locationKey],
        );
    }

    function summaryFor(locationKey: number): string {
        const granted = permissionsByLocation[locationKey] ?? [];
        const names = groups
            .flatMap((group) => [...(group.access ? [group.access] : []), ...group.children])
            .filter((permission) => granted.includes(permission.id))
            .map((permission) => permission.name);
        return names.length ? names.join(' · ') : 'No access';
    }

    function isChecked(locationKey: number, permissionId: number) {
        return (permissionsByLocation[locationKey] ?? []).includes(permissionId);
    }

    function isDisabled(locationKey: number, permissionId: number) {
        const parent = parentPermissionId(availablePermissions, permissionId);
        return parent !== undefined && !isChecked(locationKey, parent);
    }

    function renderProductGrid(rowKey: number) {
        return (
            <Grid columns={groups.length} gap="sm" collapseAt="sm">
                {groups.map((group) => renderProductCard(rowKey, group))}
            </Grid>
        );
    }

    function renderProductCard(rowKey: number, group: IPermissionGroup) {
        const active = Boolean(group.access && isChecked(rowKey, group.access.id));
        return (
            <ProductCard
                key={group.key}
                padding="xs"
                tone={active ? 'secondary' : group.access ? 'muted' : 'default'}
                account={!group.access}
            >
                <Stack gap="sm">
                    {group.access ? (
                        <Checkbox
                            label={group.label}
                            checked={isChecked(rowKey, group.access.id)}
                            onChange={() => onToggle(rowKey, group.access!.id)}
                        />
                    ) : (
                        <Stack gap="none">
                            <Text size="sm" weight="semibold">
                                {group.label}
                            </Text>
                            <Text size="xs" tone="secondary">
                                Applies across products
                            </Text>
                        </Stack>
                    )}
                    <ChildList gap="sm">
                        {group.children.map((permission) => (
                            <DisableWrap
                                key={permission.id}
                                disabled={isDisabled(rowKey, permission.id)}
                                title={permission.description}
                            >
                                <Checkbox
                                    size="small"
                                    label={permission.name}
                                    checked={isChecked(rowKey, permission.id)}
                                    onChange={() => onToggle(rowKey, permission.id)}
                                />
                            </DisableWrap>
                        ))}
                        {group.access && group.children.length === 0 && (
                            <Text size="xs" tone="secondary">
                                Full product access
                            </Text>
                        )}
                    </ChildList>
                </Stack>
            </ProductCard>
        );
    }

    function renderHeader(row: { key: number; label: string }) {
        return (
            <Stack gap="none">
                <Text weight="semibold">{row.label}</Text>
                {!isExpanded(row.key) && (
                    <Text size="xs" tone="secondary">
                        {summaryFor(row.key)}
                    </Text>
                )}
            </Stack>
        );
    }

    if (rows.length === 0) {
        return (
            <Stack gap="sm">
                <Text weight="semibold">Select Permissions</Text>
                <Text size="sm" tone="secondary">
                    Select at least one location to set permissions.
                </Text>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Text weight="semibold">Select Permissions</Text>
            {collapsible && (
                <Row justify="end" gap="sm">
                    <TextButton size={11} onClick={() => setExpandedKeys(rows.map((row) => row.key))}>
                        Expand all
                    </TextButton>
                    <Text size="xs" tone="secondary">
                        |
                    </Text>
                    <TextButton size={11} onClick={() => setExpandedKeys([])}>
                        Collapse all
                    </TextButton>
                </Row>
            )}
            {rows.map((row) =>
                collapsible ? (
                    <ExpandableCard
                        key={row.key}
                        padding="sm"
                        header={renderHeader(row)}
                        expanded={isExpanded(row.key)}
                        onExpandedChange={() => toggleExpanded(row.key)}
                    >
                        <Stack gap="sm">
                            <Row justify="end">
                                <TextButton size={11} onClick={() => onCopyToAll(row.key)}>
                                    Apply to all selected locations
                                </TextButton>
                            </Row>
                            {renderProductGrid(row.key)}
                        </Stack>
                    </ExpandableCard>
                ) : (
                    <Card key={row.key} padding="sm">
                        <Stack gap="sm">
                            {renderHeader(row)}
                            {renderProductGrid(row.key)}
                        </Stack>
                    </Card>
                ),
            )}
            <Stack gap="none">
                {groups
                    .flatMap((group) => [...(group.access ? [group.access] : []), ...group.children])
                    .map((permission) => (
                        <Text key={permission.id} size="xs" tone="secondary">
                            <b>{permission.name}:</b> {permission.description}
                        </Text>
                    ))}
            </Stack>
        </Stack>
    );
}

const ProductCard = styled(Card, { shouldForwardProp: (prop) => prop !== 'account' })<{ account: boolean }>(
    ({ account }) => ({
        borderStyle: account ? 'dashed' : 'solid',
        transition: 'background-color 120ms ease, border-color 120ms ease',
    }),
);

const ChildList = styled(Stack)({
    marginLeft: 8,
    paddingLeft: 12,
    borderLeft: `2px solid ${Colors.border}`,
});

const DisableWrap = styled('div', { shouldForwardProp: (prop) => prop !== 'disabled' })<{ disabled: boolean }>(
    ({ disabled }) => ({
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    }),
);
